import { type HotpadsApiAreaType, HpSearchFlag, HpSearchOrderBy } from '@zg-rentals/ts-hotpads-api-client';
import { buildListingsSearchRequest, type SearchListingsRequest } from '@/src/lib/api/endpoints/search';
import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type RecommendationListingsKind = 'most-popular' | 'pet-friendly';

export type ListingIdentity = {
  aliasEncoded?: string;
  lotIdEncoded?: string;
  maloneLotIdEncoded?: string;
  uriBuilding?: string;
  uriMalone?: string;
  uriV2?: string;
};

type Bounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

const DEFAULT_GEO_SPAN_DEGREES = 0.05;
const RECOMMENDATION_LIMIT = 20;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const hasBounds = (value: unknown): value is Bounds => {
  const bounds = value as Partial<Bounds> | null | undefined;
  return (
    isFiniteNumber(bounds?.minLat) &&
    isFiniteNumber(bounds?.maxLat) &&
    isFiniteNumber(bounds?.minLon) &&
    isFiniteNumber(bounds?.maxLon)
  );
};

type ListingAreas = {
  breadcrumbs?: Array<unknown>;
  links?: Array<unknown>;
  specific?: unknown;
};

type AreaIdentity = {
  id?: string | number;
  resourceId: string;
  name: string;
  type: string;
};

const getListingAreas = (listing: PropertyDetailListing | null | undefined): ListingAreas | null | undefined =>
  listing?.areas as ListingAreas | null | undefined;

const getAreaBounds = (listing: PropertyDetailListing | null | undefined): Bounds | null => {
  const areas = getListingAreas(listing);
  const candidates = [...(areas?.breadcrumbs ?? []).slice().reverse(), areas?.specific, ...(areas?.links ?? [])];

  return (candidates.find(hasBounds) as Bounds | undefined) ?? null;
};

const hasAreaIdentity = (value: unknown): value is AreaIdentity => {
  const area = value as Partial<AreaIdentity> | null | undefined;
  return Boolean(area?.resourceId && area.name && area.type);
};

// The V3 BE validates area.resourceId/name/type as required, so a listing with
// no resolvable area can't request recommendations.
const getRecommendationArea = (listing: PropertyDetailListing | null | undefined): AreaIdentity | null => {
  const areas = getListingAreas(listing);
  const candidates = [areas?.specific, ...(areas?.breadcrumbs ?? []).slice().reverse(), ...(areas?.links ?? [])];

  return candidates.find(hasAreaIdentity) ?? null;
};

export const getRecommendationBounds = (listing: PropertyDetailListing | null | undefined): Bounds | null => {
  const areaBounds = getAreaBounds(listing);
  if (areaBounds) return areaBounds;

  const lat = listing?.geo?.lat;
  const lon = listing?.geo?.lon;
  if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

  return {
    minLat: lat - DEFAULT_GEO_SPAN_DEGREES,
    maxLat: lat + DEFAULT_GEO_SPAN_DEGREES,
    minLon: lon - DEFAULT_GEO_SPAN_DEGREES,
    maxLon: lon + DEFAULT_GEO_SPAN_DEGREES,
  };
};

export const hasRecommendationSource = (listing: PropertyDetailListing | null | undefined): boolean =>
  getRecommendationBounds(listing) != null && getRecommendationArea(listing) != null;

// The V2 params sent listingTypes 'corporate,rental,room,sublet'; these flags
// are the V3 equivalents of those four rental types.
const RENTAL_TYPE_FLAGS = [
  HpSearchFlag.CORPORATE_HOUSING,
  HpSearchFlag.STANDARD_RENTAL,
  HpSearchFlag.ROOM_FOR_RENT,
  HpSearchFlag.SUBLET,
];

export const getRecommendationListingsRequest = (
  listing: PropertyDetailListing | null | undefined,
  kind: RecommendationListingsKind,
  allRentalsSlug: string,
): SearchListingsRequest | null => {
  const bounds = getRecommendationBounds(listing);
  const area = getRecommendationArea(listing);
  if (!bounds || !area) return null;

  return buildListingsSearchRequest({
    searchSlug: allRentalsSlug,
    area: {
      resourceId: area.resourceId,
      areaId: String(area.id ?? ''),
      name: area.name,
      type: area.type as HotpadsApiAreaType,
    },
    filters: {
      boundingBox: bounds,
      mustHave: [
        ...RENTAL_TYPE_FLAGS,
        ...(kind === 'pet-friendly' ? [HpSearchFlag.ALLOWS_CATS, HpSearchFlag.ALLOWS_DOGS] : []),
      ],
      ...(kind === 'most-popular' ? { orderBy: HpSearchOrderBy.VIEWS_DESC } : {}),
    },
    limit: RECOMMENDATION_LIMIT,
  });
};

export const getListingKey = (listing: ListingIdentity, index: number): string =>
  listing.aliasEncoded ??
  listing.maloneLotIdEncoded ??
  listing.lotIdEncoded ??
  listing.uriV2 ??
  listing.uriMalone ??
  listing.uriBuilding ??
  `recommendation-listing-${index}`;

export const getIdentitySet = (listing: ListingIdentity): Set<string> =>
  new Set(
    [
      listing.aliasEncoded,
      listing.maloneLotIdEncoded,
      listing.lotIdEncoded,
      listing.uriV2,
      listing.uriMalone,
      listing.uriBuilding,
    ].filter(Boolean) as Array<string>,
  );

export const hasMatchingIdentity = (listing: ListingIdentity, currentListingIds: Set<string>): boolean =>
  [
    listing.aliasEncoded,
    listing.maloneLotIdEncoded,
    listing.lotIdEncoded,
    listing.uriV2,
    listing.uriMalone,
    listing.uriBuilding,
  ].some((id) => id != null && currentListingIds.has(id));
